import { useColor, type ValidColor } from "../theme";

/** A shape from the vendored lockup, kept as whichever element the asset used. */
type LogoShape = { readonly d: string } | { readonly points: string };

/**
 * The lockup's own grid, taken from the vendored asset's viewBox. The mark fills the
 * full height, so `size` can drive both dimensions: the logo is 7.02x as wide as tall,
 * which is the brand guide's `28x by 4x` lockup around a `4x` mark.
 */
const VIEWBOX = { width: 1138.03, height: 162.01 } as const;

/**
 * The seven ELYXEER letterforms, copied verbatim from
 * `assets/brand/elyxeer-305-logo.svg` — two of them are `<polygon>`s in the brand
 * asset, and stay polygons here so the copy can be diffed against the file.
 */
export const LOGO_WORDMARK_SHAPES: readonly LogoShape[] = [
  {
    d: "M315.21,112.68c-9.47,3.14-18.92,5.39-28.36,6.76-9.44,1.37-19.09,2.06-28.95,2.06-12.6,0-23.82-1.36-33.65-4.08-9.83-2.72-18.11-6.65-24.83-11.81-6.72-5.15-11.85-11.45-15.38-18.91-3.53-7.45-5.29-15.88-5.29-25.29,0-8.74,1.78-16.85,5.34-24.33,3.56-7.48,8.6-13.98,15.13-19.49,6.53-5.52,14.41-9.83,23.65-12.94C232.11,1.55,242.41,0,253.78,0,264.26,0,273.85,1.41,282.56,4.24c8.71,2.83,16.19,7.02,22.44,12.56,6.25,5.55,11.09,12.38,14.54,20.5,3.44,8.12,5.17,17.48,5.17,28.07v7.73h-113.77c1.01,3.64,2.67,6.97,5,10,2.32,3.02,5.47,5.6,9.45,7.73,3.98,2.13,8.86,3.78,14.66,4.96,5.8,1.18,12.73,1.77,20.8,1.77,5.71,0,11.32-.35,16.81-1.05,5.49-.7,10.61-1.62,15.38-2.77,4.76-1.15,9.06-2.42,12.9-3.82,3.84-1.4,6.93-2.8,9.29-4.2v26.97ZM293.7,50.25c-.28-2.86-1.13-5.81-2.56-8.87-1.43-3.05-3.68-5.81-6.76-8.28-3.08-2.46-7.11-4.5-12.1-6.09-4.99-1.6-11.2-2.4-18.65-2.4-7,0-13.05.87-18.15,2.61-5.1,1.74-9.37,3.89-12.81,6.47-3.45,2.58-6.12,5.35-8.03,8.32-1.91,2.97-3.14,5.72-3.7,8.24h82.77Z",
  },
  {
    d: "M331.78,105.6V2.94h31.17v85.04c0,2.83,2.29,5.12,5.12,5.12h79.16v25.29h-102.66c-7.06,0-12.79-5.73-12.79-12.79",
  },
  {
    points:
      "500.12 71.25 500.12 118.39 469.04 118.39 469.04 71.25 413.07 2.94 448.03 2.94 484.66 49.41 521.13 2.94 556.09 2.94 500.12 71.25",
  },
  {
    points:
      "649.58 59.16 704.36 118.39 666.97 118.39 630.84 79.49 594.87 118.39 557.57 118.39 612.27 59.16 560.25 2.94 597.65 2.94 630.84 39.07 664.2 2.94 701.59 2.94 649.58 59.16",
  },
  {
    d: "M830.56,112.68c-9.47,3.14-18.92,5.39-28.36,6.76-9.44,1.37-19.09,2.06-28.95,2.06-12.6,0-23.82-1.36-33.65-4.08-9.83-2.72-18.11-6.65-24.83-11.81-6.72-5.15-11.85-11.45-15.38-18.91-3.53-7.45-5.29-15.88-5.29-25.29,0-8.74,1.78-16.85,5.34-24.33,3.56-7.48,8.6-13.98,15.13-19.49,6.53-5.52,14.41-9.83,23.65-12.94C747.46,1.55,757.76,0,769.14,0,779.61,0,789.2,1.41,797.91,4.24c8.71,2.83,16.19,7.02,22.44,12.56,6.25,5.55,11.09,12.38,14.54,20.5,3.44,8.12,5.17,17.48,5.17,28.07v7.73h-113.77c1.01,3.64,2.67,6.97,5,10,2.32,3.02,5.47,5.6,9.45,7.73,3.98,2.13,8.86,3.78,14.66,4.96,5.8,1.18,12.73,1.77,20.8,1.77,5.71,0,11.32-.35,16.81-1.05,5.49-.7,10.61-1.62,15.38-2.77,4.76-1.15,9.06-2.42,12.9-3.82,3.84-1.4,6.93-2.8,9.29-4.2v26.97ZM809.05,50.25c-.28-2.86-1.13-5.81-2.56-8.87-1.43-3.05-3.68-5.81-6.76-8.28-3.08-2.46-7.11-4.5-12.1-6.09-4.99-1.6-11.2-2.4-18.65-2.4-7,0-13.05.87-18.15,2.61-5.1,1.74-9.37,3.89-12.81,6.47-3.45,2.58-6.12,5.35-8.03,8.32-1.9,2.97-3.14,5.72-3.7,8.24h82.77Z",
  },
  {
    d: "M984.21,112.68c-9.47,3.14-18.92,5.39-28.36,6.76-9.44,1.37-19.09,2.06-28.95,2.06-12.6,0-23.82-1.36-33.65-4.08-9.83-2.72-18.11-6.65-24.83-11.81-6.72-5.15-11.85-11.45-15.38-18.91-3.53-7.45-5.29-15.88-5.29-25.29,0-8.74,1.78-16.85,5.34-24.33,3.56-7.48,8.6-13.98,15.13-19.49,6.53-5.52,14.41-9.83,23.65-12.94C901.11,1.55,911.41,0,922.79,0,933.26,0,942.85,1.41,951.56,4.24c8.71,2.83,16.19,7.02,22.44,12.56,6.25,5.55,11.09,12.38,14.54,20.5,3.44,8.12,5.17,17.48,5.17,28.07v7.73h-113.77c1.01,3.64,2.67,6.97,5,10,2.32,3.02,5.47,5.6,9.45,7.73,3.98,2.13,8.86,3.78,14.66,4.96,5.8,1.18,12.73,1.77,20.8,1.77,5.71,0,11.32-.35,16.81-1.05,5.49-.7,10.61-1.62,15.38-2.77,4.76-1.15,9.06-2.42,12.9-3.82,3.84-1.4,6.93-2.8,9.29-4.2v26.97ZM962.7,50.25c-.28-2.86-1.13-5.81-2.56-8.87-1.43-3.05-3.68-5.81-6.76-8.28-3.08-2.46-7.11-4.5-12.1-6.09-4.99-1.6-11.2-2.4-18.65-2.4-7,0-13.05.87-18.15,2.61-5.1,1.74-9.37,3.89-12.81,6.47-3.45,2.58-6.12,5.35-8.03,8.32-1.9,2.97-3.14,5.72-3.7,8.24h82.77Z",
  },
  {
    d: "M1032.57,118.39h-31.17V2.94h78.15c8.4,0,15.53.87,21.38,2.6,5.85,1.74,10.62,4.2,14.29,7.4,3.67,3.19,6.33,7.04,7.98,11.55,1.65,4.51,2.48,9.54,2.48,15.08,0,4.87-.69,9.13-2.06,12.77-1.37,3.64-3.22,6.77-5.55,9.37-2.32,2.61-5.03,4.8-8.11,6.6-3.08,1.79-6.33,3.28-9.75,4.45l37.81,45.63h-36.47l-34.96-42.52h-34.03v42.52ZM1094.16,39.41c0-2.24-.29-4.14-.88-5.71-.59-1.57-1.61-2.84-3.07-3.82-1.46-.98-3.4-1.69-5.84-2.14-2.44-.45-5.48-.67-9.12-.67h-42.69v24.7h42.69c3.64,0,6.68-.22,9.12-.67,2.44-.45,4.38-1.16,5.84-2.14,1.46-.98,2.48-2.25,3.07-3.82.59-1.57.88-3.47.88-5.71",
  },
] as const;

/**
 * The Dots at the lockup's scale — the same four shapes `DotsMark` draws on its own
 * 280x280 grid, exported here at 162.01/280 of that size. Two copies of one artwork
 * is the price of the mark standing alone as well as in the lockup; a test pins each
 * to its own vendored file.
 */
export const LOGO_MARK_SHAPES: readonly LogoShape[] = [
  {
    d: "M116.24,80.67c26.49,3.34,48.77-18.94,45.43-45.43C159.4,17.19,144.82,2.61,126.77.33c-26.49-3.34-48.77,18.94-45.43,45.44,2.28,18.05,16.85,32.63,34.9,34.9",
  },
  {
    d: "M35.24,80.67c26.49,3.34,48.77-18.94,45.43-45.43C78.39,17.19,63.82,2.61,45.77.33,19.27-3.01-3.01,19.27.33,45.77c2.28,18.05,16.85,32.63,34.9,34.9",
  },
  {
    d: "M35.24,161.67c26.49,3.34,48.77-18.94,45.43-45.43-2.28-18.05-16.85-32.63-34.9-34.9C19.27,78-3.01,100.28.33,126.77c2.28,18.05,16.85,32.63,34.9,34.9",
  },
  {
    d: "M126.77,81.34c-24.72-3.12-45.76,16.07-45.76,40.17h80.99c0-1.73-.1-3.49-.33-5.26-2.27-18.05-16.85-32.63-34.9-34.9",
  },
] as const;

/**
 * `30.5` — the dose, drawn as four glyphs rather than set in type. It sits under the
 * right end of the wordmark and is part of the artwork, so it carries the wordmark's
 * colour; nothing here is text the app is free to restyle.
 */
export const LOGO_SUFFIX_SHAPES: readonly LogoShape[] = [
  {
    d: "M922.77,148.48l.02-4.1h52.33c2.6,0,4.72-2.12,4.72-4.71s-2.12-4.72-4.72-4.72h-52.35l.02-4.1h52.33c4.84,0,8.81,3.97,8.81,8.82,0,2.24-.86,4.38-2.39,6.02l-.7.75.7.75c1.53,1.63,2.39,3.77,2.39,6.02,0,4.84-3.97,8.81-8.81,8.81h-52.35l.02-4.1h52.33c2.6,0,4.72-2.12,4.72-4.71s-2.12-4.72-4.72-4.72h-52.35Z",
  },
  {
    d: "M1043.98,130.85c6.84,0,12.43,5.6,12.43,12.44v6.29c0,6.84-5.6,12.43-12.43,12.43h-39.9c-6.84,0-12.44-5.6-12.44-12.43v-6.29c0-6.84,5.6-12.44,12.44-12.44h39.9ZM995.75,149.57c0,4.57,3.76,8.33,8.34,8.33h39.9c4.57,0,8.33-3.76,8.33-8.33v-6.29c0-4.57-3.76-8.34-8.33-8.34h-39.9c-4.58,0-8.34,3.76-8.34,8.34v6.29Z",
  },
  {
    d: "M1068.88,158.53c0,1.92-1.55,3.48-3.47,3.48s-3.48-1.55-3.48-3.48,1.56-3.47,3.48-3.47,3.47,1.55,3.47,3.47Z",
  },
  {
    d: "M1138.03,151.62c0,5.71-4.67,10.38-10.38,10.38h-51.24l.02-4.1h51.23c3.46,0,6.28-2.83,6.28-6.28s-2.82-6.29-6.28-6.29h-49.19c-1.13,0-2.05-.92-2.05-2.05v-10.38c0-1.13.92-2.05,2.05-2.05h59.57v4.1h-57.52v6.29h47.14c5.71,0,10.38,4.67,10.38,10.38Z",
  },
] as const;

/** Render a group's shapes; the fill is inherited from the enclosing `<g>`. */
function draw(shapes: readonly LogoShape[]) {
  return shapes.map((shape) =>
    "d" in shape ? (
      <path key={shape.d} d={shape.d} />
    ) : (
      <polygon key={shape.points} points={shape.points} />
    ),
  );
}

/**
 * The Elyxeer 30.5 lockup — the brand artwork, not type set to look like it.
 *
 * Drawn from the vendored logo rather than composed from `DotsMark` plus a
 * `<Typography>`: the wordmark is lettered, not Syncopate, and the `30.5` is a glyph
 * set on its own baseline under the R. Anything that renders it as text drifts the
 * moment the type scale moves.
 *
 * `size` is the lockup's height, which the mark fills; the width follows from the
 * asset's proportions. The two colours are separable so the mark can stay violet on a
 * surface where the wordmark has to invert.
 */
export function Wordmark({
  size = 22,
  color = "textPrimary",
  markColor = "accent",
}: {
  size?: number;
  color?: ValidColor;
  markColor?: ValidColor;
}) {
  const wordFill = useColor(color);
  const markFill = useColor(markColor);

  return (
    <svg
      height={size}
      width={size * (VIEWBOX.width / VIEWBOX.height)}
      viewBox={`0 0 ${VIEWBOX.width} ${VIEWBOX.height}`}
      role="img"
      aria-label="Elyxeer 30.5"
      // The lockup is wide: without this it is the first thing a flex row squeezes.
      style={{ display: "block", flexShrink: 0 }}
    >
      <g fill={wordFill}>{draw(LOGO_WORDMARK_SHAPES)}</g>
      <g fill={markFill}>{draw(LOGO_MARK_SHAPES)}</g>
      <g fill={wordFill}>{draw(LOGO_SUFFIX_SHAPES)}</g>
    </svg>
  );
}
